import { ConnectorHosting } from "@p0security/integrations-app-shared/components";
import { Element, Option } from "@p0security/shared/install/types";

import { GCP_ID_PATTERN } from "./gcp-hosting.ts";

// A Google Cloud-only copy of Harbor's `ConnectorHostingWithServiceAccount`,
// `isGcpServiceAccountId` and `toGcpConnectorServiceAccount`, in
// packages/integrations/harbor/shared/src/connector-hosting.ts. The beta runs
// on Cloud Run only, so the `aws` option is dropped. Change both or neither.

export const INVALID_GCP_CONNECTOR_NAME =
  "Use 6 to 30 characters for the connector name: lowercase letters, digits and hyphens, starting with a letter and ending with a letter or digit.";

/** Whether `id`, ignoring surrounding whitespace, can be a Google Cloud
 * service account's ID. */
export const isGcpServiceAccountId = (id: unknown): id is string =>
  typeof id === "string" && GCP_ID_PATTERN.test(id.trim());

/** The email of the service account whose ID is the connector's name, in the
 * project `projectId`. Each value is trimmed, as P0 stores it. */
export const toGcpConnectorServiceAccount = (args: {
  connectorName: string;
  projectId: string;
}) => {
  const { connectorName, projectId } = args;
  return `${connectorName.trim()}@${projectId.trim()}.iam.gserviceaccount.com`;
};

const { gcp } = ConnectorHosting.options;

/** Where a Jira (AI) connector runs: Cloud Run only, as the service account
 * named after the connector.
 *
 * `connectorServiceAccount` is the email of that account. P0 sets it when the
 * item is created, and no request can: wire `gcpConnectorHostingAssembler` as
 * the option's assembler.
 */
export const JiraAiConnectorHosting = Element({
  ...ConnectorHosting,
  default: "gcp",
  options: {
    gcp: Option({
      ...gcp,
      maturity: "beta",
      schema: {
        ...gcp.schema,
        // Cloud Run takes any service account ID as a service's name.
        connectorName: Element({
          ...gcp.schema.connectorName,
          validator: async (_id: string, connectorName: string) =>
            isGcpServiceAccountId(connectorName)
              ? undefined
              : INVALID_GCP_CONNECTOR_NAME,
        }),
        // Fixed at creation. Hidden rather than generated, because the
        // new-item form would show a generated field that's fixed at creation.
        connectorServiceAccount: Element({
          description:
            "The service account that the connector's Cloud Run service runs as, named after the connector",
          label: "Connector service account",
          step: "new",
          type: "hidden",
        }),
      },
    }),
  },
});
